<template>
  <div class="import-container">
    <h5>证明信息上传</h5>

    <div class="upload-actions">
      <el-select v-model="selectedSemesterUuid" placeholder="请选择学期" class="semester-select">
        <el-option
          v-for="(semester, index) in semesters"
          :key="semester.uuid"
          :label="semester.name"
          :value="semester.uuid"
        >
          <span>{{ semester.name }}</span>
          <template v-if="index === 0">
            <el-tag type="primary" style="margin-left: 10px;">当前学期</el-tag>
          </template>
        </el-option>
      </el-select>

      <el-upload
        class="upload-excel"
        ref="uploadRef"
        action=""
        :http-request="handleUpload"
        :before-upload="beforeUpload"
        :on-change="handleFileChange"
        :on-remove="handleRemove"
        accept=".xlsx, .xls"
        :auto-upload="false"
      >
        <template #trigger>
          <el-button type="primary">选择Excel文件</el-button>
        </template>
      </el-upload>

      <el-button
        type="success"
        @click="submitFile"
        :loading="loading"
      >
        上传到服务器
      </el-button>
    </div>

    <el-dialog
      v-model="previewDialogVisible"
      title="上传内容预览"
      width="90%"
      :close-on-click-modal="false"
    >
      <el-alert
        v-if="separatorErrors.length"
        class="separator-alert"
        title="检测到姓名分隔符不规范"
        type="error"
        show-icon
        :closable="false"
      >
        <template #default>
          执教者或指导教师存在空格、逗号、分号、斜杠等非法分隔符，请使用“、”分隔多个姓名。
        </template>
      </el-alert>

      <el-table
        v-if="separatorErrors.length"
        :data="separatorErrors"
        border
        class="error-table"
        style="width:100%"
      >
        <el-table-column prop="sheetName" label="工作表" width="150" />
        <el-table-column prop="rowNumber" label="行号" width="90" />
        <el-table-column prop="field" label="字段" width="120" />
        <el-table-column prop="originalValue" label="错误值" min-width="180" show-overflow-tooltip />
        <el-table-column prop="fixedValue" label="程序修改值" min-width="180" show-overflow-tooltip />
      </el-table>

      <el-alert
        v-else-if="autoFixApplied"
        class="separator-alert"
        title="已应用程序修改，预览表格中的姓名分隔符已替换为“、”。"
        type="success"
        show-icon
        :closable="false"
      />

      <el-tabs v-model="activeTab">
        <el-tab-pane
          v-for="sheet in sheetConfigs"
          :key="sheet.key"
          :label="sheet.tabLabel"
          :name="sheet.key"
        >
          <el-table
            :data="previewData[sheet.key]"
            border
            style="width:100%"
            :cell-class-name="previewCellClassName"
          >
            <el-table-column
              v-for="column in sheet.columns"
              :key="column.prop"
              :prop="column.prop"
              :label="column.label"
              :width="column.width"
              :min-width="column.minWidth"
              show-overflow-tooltip
            >
              <template #default="scope">
                <span v-if="column.type === 'date'">{{ formatPreviewDate(scope.row) }}</span>
                <span v-else>{{ scope.row[column.prop] }}</span>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>

      <template #footer>
        <el-button
          v-if="separatorErrors.length"
          type="warning"
          @click="applyAutoFix"
        >
          应用程序修改
        </el-button>
        <el-button @click="cancelPreview">取消</el-button>
        <el-button
          type="success"
          :disabled="separatorErrors.length > 0"
          :loading="loading"
          @click="confirmUpload"
        >
          提交上传
        </el-button>
      </template>
    </el-dialog>

    <el-dialog
      v-model="errorDialogVisible"
      title="错误信息"
      width="30%"
    >
      <span>{{ errorMessage }}</span>
      <template #footer>
        <el-button @click="errorDialogVisible = false">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import axios from 'axios';
import * as XLSX from 'xlsx';

const SHEET_CONFIGS = [
  {
    sheetName: '校级开课汇总',
    key: 'school_course_summary',
    tabLabel: '校级开课',
    columns: [
      { prop: 'subject', label: '学科', width: 100 },
      { prop: 'topic', label: '课题', minWidth: 180 },
      { prop: 'teacher', label: '执教者', width: 150 },
      { prop: 'level', label: '级别', width: 100 },
      { prop: 'mentor', label: '指导教师', width: 160 },
      { prop: 'date', label: '日期', type: 'date', width: 120 },
      { prop: 'remark', label: '备注', minWidth: 160 },
    ],
  },
  {
    sheetName: '校级讲座',
    key: 'school_lecture',
    tabLabel: '校级讲座',
    columns: [
      { prop: 'subject', label: '学科', width: 100 },
      { prop: 'topic', label: '课题', minWidth: 180 },
      { prop: 'teacher', label: '执教者', width: 150 },
      { prop: 'mentor', label: '指导教师', width: 160 },
      { prop: 'date', label: '日期', type: 'date', width: 120 },
      { prop: 'level', label: '级别', width: 100 },
    ],
  },
  {
    sheetName: '校级主备汇总',
    key: 'school_preparation_summary',
    tabLabel: '校级主备',
    columns: [
      { prop: 'subject', label: '学科', width: 100 },
      { prop: 'topic', label: '课题', minWidth: 180 },
      { prop: 'teacher', label: '执教者', width: 150 },
      { prop: 'mentor', label: '指导教师', width: 160 },
      { prop: 'date', label: '日期', type: 'date', width: 120 },
      { prop: 'remark', label: '备注', minWidth: 160 },
    ],
  },
  {
    sheetName: '跟岗汇总',
    key: 'internship_certificate',
    tabLabel: '跟岗实践',
    columns: [
      { prop: 'subject', label: '学科', width: 100 },
      { prop: 'topic', label: '内容', minWidth: 180 },
      { prop: 'teacher', label: '执教者', width: 150 },
      { prop: 'mentor', label: '指导教师', width: 160 },
      { prop: 'date', label: '日期', type: 'date', width: 120 },
      { prop: 'remark', label: '备注', minWidth: 160 },
    ],
  },
];

const SHEET_CONFIG_BY_NAME = SHEET_CONFIGS.reduce((result, config) => {
  result[config.sheetName] = config;
  return result;
}, {});

const NAME_FIELD_CONFIGS = [
  { header: '执教者', prop: 'teacher' },
  { header: '指导教师', prop: 'mentor' },
];

const ILLEGAL_SEPARATOR_REGEXP = /[\s,，;；/\\|]+/;
const ALL_NAME_SEPARATOR_REGEXP = /[、\s,，;；/\\|]+/;

function createEmptyPreviewData() {
  return SHEET_CONFIGS.reduce((result, config) => {
    result[config.key] = [];
    return result;
  }, {});
}

export default {
  name: 'ImportPage',
  data() {
    return {
      file: null,
      uploadFile: null,
      workbook: null,
      loading: false,
      semesters: [],
      selectedSemesterUuid: '',
      errorDialogVisible: false,
      errorMessage: '',
      previewDialogVisible: false,
      activeTab: SHEET_CONFIGS[0].key,
      previewData: createEmptyPreviewData(),
      separatorErrors: [],
      autoFixApplied: false,
      sheetConfigs: SHEET_CONFIGS,
    };
  },
  mounted() {
    this.fetchSemesters();
  },
  methods: {
    async fetchSemesters() {
      try {
        const response = await axios.get('/api/semesters');
        this.semesters = response.data.semesters;
      } catch (error) {
        console.error('获取学期数据失败:', error);
        ElMessage.error('获取学期数据失败');
      }
    },
    beforeUpload(file) {
      this.setSelectedFile(file);
      return false;
    },
    handleFileChange(uploadFile) {
      this.setSelectedFile(uploadFile);
    },
    setSelectedFile(file) {
      const selectedFile = file && (file.raw || file);
      if (!selectedFile) {
        return;
      }
      this.file = selectedFile;
      this.uploadFile = selectedFile;
      this.resetPreviewState();
    },
    handleRemove() {
      this.file = null;
      this.uploadFile = null;
      this.resetPreviewState();
    },
    handleUpload() {
      // 手动上传前先进入预览确认流程
    },
    async submitFile() {
      if (!this.file) {
        ElMessage.error('请先选择一个文件');
        return;
      }
      if (!this.selectedSemesterUuid) {
        ElMessage.error('请选择一个学期');
        return;
      }

      this.loading = true;
      try {
        await this.preparePreview();
        this.previewDialogVisible = true;
      } catch (error) {
        this.errorMessage = error.message || '无法预览Excel文件';
        this.errorDialogVisible = true;
      } finally {
        this.loading = false;
      }
    },
    async preparePreview() {
      const workbook = await this.readWorkbook(this.file);
      this.workbook = workbook;
      this.uploadFile = this.file;
      this.previewData = createEmptyPreviewData();
      this.separatorErrors = [];
      this.autoFixApplied = false;

      if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error('Excel文件没有包含任何工作表');
      }

      workbook.SheetNames.forEach((sheetName) => {
        const config = SHEET_CONFIG_BY_NAME[sheetName];
        if (!config) {
          throw new Error(`未识别的工作表: ${sheetName}`);
        }

        const { rows, headerIndex, headers, headerMap } = this.readSheetRows(workbook.Sheets[sheetName]);
        if (headerIndex === -1) {
          throw new Error(`工作表 ${sheetName} 是空的`);
        }

        const missingNameField = NAME_FIELD_CONFIGS.find(field => !headerMap[field.header]);
        if (missingNameField) {
          throw new Error(`工作表 ${sheetName} 缺少必要的表头`);
        }

        const dataRows = rows
          .slice(headerIndex + 1)
          .filter(rowInfo => this.hasRowContent(rowInfo.row));

        this.previewData[config.key] = dataRows.map((rowInfo) => {
          const rawRow = this.createRawRow(headers, rowInfo.row);
          const previewRow = this.mapPreviewRow(rawRow, rowInfo.rowNumber);
          this.collectSeparatorErrors(sheetName, config.key, headerMap, rawRow, previewRow);
          return previewRow;
        });
      });

      const firstDataSheet = SHEET_CONFIGS.find(config => this.previewData[config.key].length > 0);
      this.activeTab = firstDataSheet ? firstDataSheet.key : SHEET_CONFIGS[0].key;
    },
    readWorkbook(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const data = new Uint8Array(event.target.result);
            resolve(XLSX.read(data, { type: 'array', cellDates: false }));
          } catch (error) {
            reject(new Error(`无法读取Excel文件: ${error.message}`));
          }
        };
        reader.onerror = () => reject(new Error('无法读取Excel文件'));
        reader.readAsArrayBuffer(file);
      });
    },
    readSheetRows(sheet) {
      const rows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: '',
        raw: false,
      }).map((row, index) => ({
        row,
        rowNumber: index + 1,
      }));

      const headerIndex = rows.findIndex(rowInfo => this.hasRowContent(rowInfo.row));
      if (headerIndex === -1) {
        return { rows, headerIndex, headers: [], headerMap: {} };
      }

      const headers = rows[headerIndex].row.map(value => this.cellToText(value).trim());
      const headerMap = headers.reduce((result, header, index) => {
        if (header) {
          result[header] = {
            index,
            cellAddress: XLSX.utils.encode_cell({ r: headerIndex, c: index }),
          };
        }
        return result;
      }, {});

      return { rows, headerIndex, headers, headerMap };
    },
    createRawRow(headers, row) {
      return headers.reduce((result, header, index) => {
        if (header) {
          result[header] = this.cellToText(row[index]).trim();
        }
        return result;
      }, {});
    },
    mapPreviewRow(rawRow, rowNumber) {
      return {
        __rowNumber: rowNumber,
        __invalidFields: {},
        year: rawRow['年'] || '',
        month: rawRow['月'] || '',
        day: rawRow['日'] || '',
        subject: rawRow['学科'] || '',
        topic: rawRow['课题（年级、学科、内容）'] || '',
        level: rawRow['级别'] || '',
        teacher: rawRow['执教者'] || '',
        mentor: rawRow['指导教师'] || '',
        type: rawRow['类型'] || '',
        remark: rawRow['备注'] || '',
        location: rawRow['地点'] || '',
      };
    },
    collectSeparatorErrors(sheetName, previewKey, headerMap, rawRow, previewRow) {
      NAME_FIELD_CONFIGS.forEach((field) => {
        const checkResult = this.getNameSeparatorFix(rawRow[field.header]);
        if (!checkResult.invalid) {
          return;
        }

        const columnInfo = headerMap[field.header];
        previewRow.__invalidFields[field.prop] = true;
        this.separatorErrors.push({
          id: `${sheetName}-${previewRow.__rowNumber}-${field.header}`,
          sheetName,
          previewKey,
          previewProp: field.prop,
          rowNumber: previewRow.__rowNumber,
          field: field.header,
          originalValue: checkResult.originalValue,
          fixedValue: checkResult.fixedValue,
          cellAddress: columnInfo
            ? XLSX.utils.encode_cell({ r: previewRow.__rowNumber - 1, c: columnInfo.index })
            : '',
        });
      });
    },
    getNameSeparatorFix(value) {
      const originalValue = this.cellToText(value).trim();
      if (!originalValue || originalValue.toLowerCase() === 'nan') {
        return { invalid: false, originalValue, fixedValue: originalValue };
      }

      const parts = originalValue
        .split(ALL_NAME_SEPARATOR_REGEXP)
        .map(item => item.trim())
        .filter(Boolean);

      if (parts.length <= 1 || !ILLEGAL_SEPARATOR_REGEXP.test(originalValue)) {
        return { invalid: false, originalValue, fixedValue: originalValue };
      }

      return {
        invalid: true,
        originalValue,
        fixedValue: parts.join('、'),
      };
    },
    applyAutoFix() {
      this.separatorErrors.forEach((error) => {
        const sheet = this.workbook.Sheets[error.sheetName];
        if (sheet && error.cellAddress) {
          sheet[error.cellAddress] = {
            t: 's',
            v: error.fixedValue,
            w: error.fixedValue,
          };
        }

        const row = this.previewData[error.previewKey]
          .find(item => item.__rowNumber === error.rowNumber);
        if (row) {
          row[error.previewProp] = error.fixedValue;
          row.__invalidFields[error.previewProp] = false;
        }
      });

      const fixedWorkbook = XLSX.write(this.workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      const fixedFileName = this.getFixedFileName(this.file.name);
      this.uploadFile = new File([fixedWorkbook], fixedFileName, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      this.separatorErrors = [];
      this.autoFixApplied = true;
      ElMessage.success('已应用程序修改');
    },
    async confirmUpload() {
      if (this.separatorErrors.length > 0) {
        ElMessage.error('请先应用程序修改，或取消后修改原文件');
        return;
      }

      this.loading = true;
      const formData = new FormData();
      formData.append('file', this.uploadFile || this.file);
      formData.append('semesterUuid', this.selectedSemesterUuid);

      try {
        const response = await axios.post('/api/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        ElMessage.success(response.data.message || '文件上传成功');
        this.resetUploadState();
      } catch (error) {
        this.errorMessage = error.response?.data?.error || '文件上传失败';
        this.errorDialogVisible = true;
      } finally {
        this.loading = false;
      }
    },
    cancelPreview() {
      this.previewDialogVisible = false;
    },
    resetUploadState() {
      this.file = null;
      this.uploadFile = null;
      this.selectedSemesterUuid = '';
      this.previewDialogVisible = false;
      this.resetPreviewState();
      if (this.$refs.uploadRef) {
        this.$refs.uploadRef.clearFiles();
      }
    },
    resetPreviewState() {
      this.workbook = null;
      this.previewData = createEmptyPreviewData();
      this.separatorErrors = [];
      this.autoFixApplied = false;
      this.activeTab = SHEET_CONFIGS[0].key;
    },
    previewCellClassName({ row, column }) {
      return row.__invalidFields && row.__invalidFields[column.property]
        ? 'invalid-name-cell'
        : '';
    },
    formatPreviewDate(row) {
      const dateParts = [row.year, row.month, row.day].filter(item => item !== '');
      return dateParts.length ? dateParts.join('/') : '';
    },
    hasRowContent(row) {
      return row.some(value => this.cellToText(value).trim() !== '');
    },
    cellToText(value) {
      return value === null || value === undefined ? '' : String(value);
    },
    getFixedFileName(fileName) {
      if (!fileName) {
        return 'fixed-upload.xlsx';
      }
      return fileName.toLowerCase().endsWith('.xlsx')
        ? fileName
        : fileName.replace(/\.[^.]+$/, '') + '.xlsx';
    },
  },
};
</script>

<style scoped>
.import-container {
  text-align: center;
}

.upload-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.semester-select {
  width: 220px;
}

.separator-alert {
  margin-bottom: 12px;
}

.error-table {
  margin-bottom: 16px;
}

:deep(.invalid-name-cell) {
  background-color: #fff2f0;
  color: #c45656;
  font-weight: 600;
}
</style>
